import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useClient } from '@/contexts/ClientContext';
import { SidebarLayout, SidebarItem } from '@/components/layout/SidebarLayout';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
// Simple freshness display - not using DataFreshnessIndicator for now

/**
 * DataResearchPage - Central hub for data sources and research
 *
 * Implements Phase 6: Data Sources Move
 * - Data Sources tab: Shows all data sources with freshness indicators
 * - Research tab: Survey results and research insights
 * - Value Comparison tab: Compare incremental value of data sources
 */

function getSidebarItems(language: string): SidebarItem[] {
  const t = language === 'es';

  return [
    {
      id: 'sources',
      icon: <span className="text-lg">🗂️</span>,
      name: t ? 'Fuentes de Datos' : 'Data Sources',
      description: t ? 'Orígenes y frescura' : 'Origins and freshness',
      subItems: [
        { id: 'all-sources', label: t ? 'Todas' : 'All Sources' },
        { id: 'syndicated', label: t ? 'Sindicados' : 'Syndicated' },
        { id: 'internal', label: t ? 'Internos' : 'Internal' },
        { id: 'surveys', label: t ? 'Encuestas' : 'Surveys' },
      ],
    },
    {
      id: 'research',
      icon: <span className="text-lg">🔬</span>,
      name: t ? 'Investigación' : 'Research',
      description: t ? 'Resultados de estudios' : 'Study results',
      subItems: [
        { id: 'all-research', label: t ? 'Todos' : 'All Studies' },
        { id: 'consumer', label: t ? 'Consumidor' : 'Consumer' },
        { id: 'product', label: t ? 'Producto' : 'Product' },
        { id: 'brand', label: t ? 'Marca' : 'Brand' },
      ],
    },
    {
      id: 'value',
      icon: <span className="text-lg">💎</span>,
      name: t ? 'Valor de Datos' : 'Data Value',
      description: t ? 'ROI por fuente' : 'ROI by source',
      subItems: [
        { id: 'comparison', label: t ? 'Comparación' : 'Comparison' },
        { id: 'recommendations', label: t ? 'Recomendaciones' : 'Recommendations' },
      ],
    },
  ];
}

export function DataResearchPage() {
  const { language } = useLanguage();
  const { selectedClient } = useClient();
  const [activeSection, setActiveSection] = useState('sources');
  const [activeTab, setActiveTab] = useState('all-sources');
  const t = language === 'es';

  const sidebarItems = getSidebarItems(language);

  const handleSectionChange = (sectionId: string) => {
    setActiveSection(sectionId);
    const section = sidebarItems.find(s => s.id === sectionId);
    if (section?.subItems?.[0]) {
      setActiveTab(section.subItems[0].id);
    }
  };

  // Welcome state if no client selected
  if (!selectedClient) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <div className="text-6xl mb-4">🗂️</div>
        <h1 className="text-2xl font-bold text-white mb-2">
          {t ? 'Datos e Investigación' : 'Data & Research'}
        </h1>
        <p className="text-text-secondary mb-6">
          {t
            ? 'Selecciona un cliente para ver sus fuentes de datos y resultados de investigación.'
            : 'Select a client to view their data sources and research results.'}
        </p>
      </div>
    );
  }

  const activeItem = sidebarItems.find(s => s.id === activeSection);

  return (
    <SidebarLayout
      title={t ? 'Datos e Investigación' : 'Data & Research'}
      subtitle={selectedClient.client_name}
      items={sidebarItems}
      activeItemId={activeSection}
      activeSubItemId={activeTab}
      onItemChange={handleSectionChange}
      onSubItemChange={setActiveTab}
    >
      <DataResearchContent
        section={activeSection}
        tab={activeTab}
        language={language}
        clientId={selectedClient.client_id}
      />
    </SidebarLayout>
  );
}

function DataResearchContent({
  section,
  tab,
  language,
  clientId,
}: {
  section: string;
  tab: string;
  language: string;
  clientId: string;
}) {
  const t = language === 'es';

  switch (section) {
    case 'sources':
      return <DataSourcesContent tab={tab} language={language} />;
    case 'research':
      return <ResearchContent tab={tab} language={language} />;
    case 'value':
      return <DataValueContent tab={tab} language={language} />;
    default:
      return <DataSourcesContent tab={tab} language={language} />;
  }
}

// ============================================================================
// DATA SOURCES CONTENT
// ============================================================================

function DataSourcesContent({ tab, language }: { tab: string; language: string }) {
  const t = language === 'es';

  const dataSources = [
    {
      id: 'niq',
      name: 'NielsenIQ',
      type: 'syndicated',
      description: t ? 'Datos de retail y consumidor' : 'Retail and consumer data',
      lastUpdated: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
      coverage: 85,
      metrics: ['Market Share', 'Volume', 'Price Index', 'Distribution'],
    },
    {
      id: 'circana',
      name: 'Circana (IRI)',
      type: 'syndicated',
      description: t ? 'Panel de consumidor y retail' : 'Consumer and retail panel',
      lastUpdated: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000), // 14 days ago
      coverage: 72,
      metrics: ['Household Penetration', 'Loyalty', 'Switching'],
    },
    {
      id: 'internal-sales',
      name: t ? 'Ventas Internas' : 'Internal Sales',
      type: 'internal',
      description: t ? 'Datos de sell-out propios' : 'Own sell-out data',
      lastUpdated: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), // 1 day ago
      coverage: 100,
      metrics: ['Revenue', 'Volume', 'Margin', 'SKU Performance'],
    },
    {
      id: 'consumer-survey',
      name: t ? 'Encuesta de Consumidor' : 'Consumer Survey',
      type: 'surveys',
      description: t ? 'Estudio de preferencias Q4 2025' : 'Q4 2025 preference study',
      lastUpdated: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000), // 45 days ago
      coverage: 60,
      metrics: ['Brand Awareness', 'Preference', 'Purchase Intent'],
    },
    {
      id: 'price-scraper',
      name: t ? 'Monitoreo de Precios' : 'Price Monitoring',
      type: 'internal',
      description: t ? 'Scraping de precios en línea' : 'Online price scraping',
      lastUpdated: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
      coverage: 90,
      metrics: ['Competitor Prices', 'Promotions', 'Availability'],
    },
  ];

  const filteredSources = tab === 'all-sources'
    ? dataSources
    : dataSources.filter(s => s.type === tab);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">
            {t ? 'Fuentes de Datos' : 'Data Sources'}
          </h2>
          <p className="text-sm text-text-secondary">
            {filteredSources.length} {t ? 'fuentes disponibles' : 'sources available'}
          </p>
        </div>
        <button className="px-4 py-2 bg-primary-500/20 text-primary-400 rounded-lg text-sm font-medium hover:bg-primary-500/30 transition-colors">
          {t ? '+ Agregar Fuente' : '+ Add Source'}
        </button>
      </div>

      {/* Source Cards */}
      <div className="space-y-4">
        {filteredSources.map((source) => (
          <DataSourceCard key={source.id} source={source} language={language} />
        ))}
      </div>

      {filteredSources.length === 0 && (
        <div className="card p-8 text-center">
          <div className="text-4xl mb-4">📭</div>
          <p className="text-text-secondary">
            {t ? 'No hay fuentes en esta categoría' : 'No sources in this category'}
          </p>
        </div>
      )}
    </div>
  );
}

function FreshnessIndicator({ lastUpdated, language }: { lastUpdated: Date; language: string }) {
  const t = language === 'es';
  const daysSinceUpdate = Math.floor((Date.now() - lastUpdated.getTime()) / (1000 * 60 * 60 * 24));

  let statusColor = 'text-success-400';
  let statusText = t ? 'Actualizado' : 'Current';

  if (daysSinceUpdate > 30) {
    statusColor = 'text-danger-400';
    statusText = t ? 'Desactualizado' : 'Stale';
  } else if (daysSinceUpdate > 14) {
    statusColor = 'text-warning-400';
    statusText = t ? 'Revisar' : 'Review';
  }

  return (
    <div className="flex items-center gap-2 text-xs">
      <span className={`w-2 h-2 rounded-full ${statusColor.replace('text-', 'bg-')}`} />
      <span className={statusColor}>{statusText}</span>
      <span className="text-text-tertiary">
        {daysSinceUpdate === 0
          ? (t ? 'Hoy' : 'Today')
          : daysSinceUpdate === 1
          ? (t ? 'Ayer' : 'Yesterday')
          : `${daysSinceUpdate}d`}
      </span>
    </div>
  );
}

function DataSourceCard({ source, language }: { source: any; language: string }) {
  const t = language === 'es';
  const daysSinceUpdate = Math.floor((Date.now() - source.lastUpdated.getTime()) / (1000 * 60 * 60 * 24));
  const isStale = daysSinceUpdate > 30;

  const typeLabels: Record<string, string> = {
    syndicated: t ? 'Sindicado' : 'Syndicated',
    internal: t ? 'Interno' : 'Internal',
    surveys: t ? 'Encuesta' : 'Survey',
  };

  const typeColors: Record<string, string> = {
    syndicated: 'bg-primary-500/20 text-primary-400',
    internal: 'bg-success-500/20 text-success-400',
    surveys: 'bg-warning-500/20 text-warning-400',
  };

  return (
    <div className={`card p-5 ${isStale ? 'border-warning-500/30' : ''}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-surface-600 rounded-lg flex items-center justify-center text-lg">
            {source.type === 'syndicated' ? '📊' : source.type === 'internal' ? '🏢' : '📋'}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-white">{source.name}</h3>
              <span className={`text-xs px-2 py-0.5 rounded ${typeColors[source.type]}`}>
                {typeLabels[source.type]}
              </span>
            </div>
            <p className="text-sm text-text-secondary">{source.description}</p>
          </div>
        </div>

        <FreshnessIndicator
          lastUpdated={source.lastUpdated}
          language={language}
        />
      </div>

      {/* Coverage Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-text-tertiary">{t ? 'Cobertura' : 'Coverage'}</span>
          <span className="text-white font-medium">{source.coverage}%</span>
        </div>
        <div className="h-1.5 bg-surface-600 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary-500 rounded-full"
            style={{ width: `${source.coverage}%` }}
          />
        </div>
      </div>

      {/* Metrics */}
      <div className="flex flex-wrap gap-1.5">
        {source.metrics.map((metric: string, idx: number) => (
          <span
            key={idx}
            className="text-xs px-2 py-0.5 bg-surface-600 text-text-secondary rounded"
          >
            {metric}
          </span>
        ))}
      </div>

      {isStale && (
        <div className="mt-4 p-3 bg-warning-500/10 border border-warning-500/20 rounded-lg">
          <p className="text-xs text-warning-400">
            {t
              ? `Esta fuente no se ha actualizado en ${daysSinceUpdate} días. Considere solicitar una actualización.`
              : `This source hasn't been updated in ${daysSinceUpdate} days. Consider requesting an update.`}
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// RESEARCH CONTENT
// ============================================================================

function ResearchContent({ tab, language }: { tab: string; language: string }) {
  const t = language === 'es';

  const researchStudies = [
    {
      id: 'consumer-q4-2025',
      name: t ? 'Estudio de Consumidor Q4 2025' : 'Consumer Study Q4 2025',
      type: 'consumer',
      date: new Date(2025, 11, 15),
      sampleSize: 1200,
      status: 'completed',
      keyFindings: [
        t ? 'Preferencia por productos saludables aumentó 12%' : 'Preference for healthy products increased 12%',
        t ? 'Sensibilidad al precio baja en segmento premium' : 'Low price sensitivity in premium segment',
        t ? 'E-commerce preferido por menores de 35' : 'E-commerce preferred by under 35s',
      ],
    },
    {
      id: 'product-test-2025',
      name: t ? 'Test de Producto - Nueva Línea' : 'Product Test - New Line',
      type: 'product',
      date: new Date(2025, 10, 20),
      sampleSize: 400,
      status: 'completed',
      keyFindings: [
        t ? 'Alta aceptación del nuevo sabor (78%)' : 'High acceptance of new flavor (78%)',
        t ? 'Precio percibido como justo' : 'Price perceived as fair',
        t ? 'Empaque necesita mejoras' : 'Packaging needs improvements',
      ],
    },
    {
      id: 'brand-tracking-2025',
      name: t ? 'Brand Tracking Anual' : 'Annual Brand Tracking',
      type: 'brand',
      date: new Date(2025, 9, 1),
      sampleSize: 2500,
      status: 'completed',
      keyFindings: [
        t ? 'Awareness aumentó 5pp vs. año anterior' : 'Awareness increased 5pp vs. prior year',
        t ? 'NPS mejoró de 35 a 42' : 'NPS improved from 35 to 42',
        t ? 'Percepción de calidad estable' : 'Quality perception stable',
      ],
    },
  ];

  const filteredStudies = tab === 'all-research'
    ? researchStudies
    : researchStudies.filter(s => s.type === tab);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">
            {t ? 'Investigación' : 'Research'}
          </h2>
          <p className="text-sm text-text-secondary">
            {filteredStudies.length} {t ? 'estudios disponibles' : 'studies available'}
          </p>
        </div>
        <button className="px-4 py-2 bg-primary-500/20 text-primary-400 rounded-lg text-sm font-medium hover:bg-primary-500/30 transition-colors">
          {t ? '+ Solicitar Estudio' : '+ Request Study'}
        </button>
      </div>

      <div className="space-y-4">
        {filteredStudies.map((study) => (
          <ResearchCard key={study.id} study={study} language={language} />
        ))}
      </div>
    </div>
  );
}

function ResearchCard({ study, language }: { study: any; language: string }) {
  const t = language === 'es';

  const typeLabels: Record<string, string> = {
    consumer: t ? 'Consumidor' : 'Consumer',
    product: t ? 'Producto' : 'Product',
    brand: t ? 'Marca' : 'Brand',
  };

  return (
    <div className="card p-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-white">{study.name}</h3>
            <span className="text-xs px-2 py-0.5 bg-primary-500/20 text-primary-400 rounded">
              {typeLabels[study.type]}
            </span>
          </div>
          <div className="flex items-center gap-4 text-xs text-text-tertiary">
            <span>{study.date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US')}</span>
            <span>n={study.sampleSize.toLocaleString()}</span>
            <span className="text-success-400">{t ? 'Completado' : 'Completed'}</span>
          </div>
        </div>
        <button className="text-sm text-primary-400 hover:text-primary-300">
          {t ? 'Ver Reporte' : 'View Report'} →
        </button>
      </div>

      <div>
        <h4 className="text-xs text-text-tertiary uppercase tracking-wide mb-2">
          {t ? 'Hallazgos Clave' : 'Key Findings'}
        </h4>
        <ul className="space-y-1.5">
          {study.keyFindings.map((finding: string, idx: number) => (
            <li key={idx} className="flex items-start gap-2 text-sm text-text-secondary">
              <span className="text-primary-400 mt-0.5">•</span>
              <span>{finding}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ============================================================================
// DATA VALUE CONTENT
// ============================================================================

function DataValueContent({ tab, language }: { tab: string; language: string }) {
  const t = language === 'es';

  if (tab === 'comparison') {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold text-white">
            {t ? 'Comparación de Valor' : 'Value Comparison'}
          </h2>
          <p className="text-sm text-text-secondary">
            {t ? 'ROI incremental por fuente de datos' : 'Incremental ROI by data source'}
          </p>
        </div>

        <div className="card p-6">
          <h3 className="font-semibold text-white mb-4">
            {t ? 'Valor por Fuente' : 'Value by Source'}
          </h3>

          <div className="space-y-4">
            {[
              { name: 'NielsenIQ', value: 2.4, cost: 150000 },
              { name: t ? 'Encuestas' : 'Surveys', value: 1.8, cost: 45000 },
              { name: t ? 'Datos Internos' : 'Internal Data', value: 3.1, cost: 25000 },
              { name: 'Price Scraping', value: 2.8, cost: 12000 },
            ].map((source) => (
              <div key={source.name} className="flex items-center gap-4">
                <span className="w-32 text-sm text-text-secondary">{source.name}</span>
                <div className="flex-1 h-6 bg-surface-600 rounded overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary-500 to-primary-400 flex items-center justify-end px-2"
                    style={{ width: `${(source.value / 3.5) * 100}%` }}
                  >
                    <span className="text-xs font-medium text-white">{source.value}x</span>
                  </div>
                </div>
                <span className="w-24 text-sm text-text-tertiary text-right">
                  ${(source.cost / 1000).toFixed(0)}K/yr
                </span>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-success-500/10 border border-success-500/20 rounded-lg">
            <p className="text-sm text-success-400">
              {t
                ? 'Los datos internos ofrecen el mejor ROI. Considere invertir en infraestructura de datos propios.'
                : 'Internal data offers the best ROI. Consider investing in own data infrastructure.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white">
          {t ? 'Recomendaciones' : 'Recommendations'}
        </h2>
        <p className="text-sm text-text-secondary">
          {t ? 'Optimización de inversión en datos' : 'Data investment optimization'}
        </p>
      </div>

      <div className="space-y-4">
        <div className="card p-5 border-l-4 border-l-success-500">
          <h3 className="font-semibold text-white mb-2">
            {t ? 'Invertir Más' : 'Invest More'}
          </h3>
          <p className="text-sm text-text-secondary mb-3">
            {t
              ? 'Aumentar frecuencia de actualización de datos internos de ventas.'
              : 'Increase update frequency of internal sales data.'}
          </p>
          <span className="text-xs text-success-400">{t ? 'ROI esperado: 3.5x' : 'Expected ROI: 3.5x'}</span>
        </div>

        <div className="card p-5 border-l-4 border-l-warning-500">
          <h3 className="font-semibold text-white mb-2">
            {t ? 'Evaluar' : 'Evaluate'}
          </h3>
          <p className="text-sm text-text-secondary mb-3">
            {t
              ? 'Considerar reducir frecuencia de estudios de brand tracking de trimestral a semestral.'
              : 'Consider reducing brand tracking frequency from quarterly to bi-annual.'}
          </p>
          <span className="text-xs text-warning-400">{t ? 'Ahorro potencial: $30K/año' : 'Potential savings: $30K/year'}</span>
        </div>

        <div className="card p-5 border-l-4 border-l-primary-500">
          <h3 className="font-semibold text-white mb-2">
            {t ? 'Nueva Oportunidad' : 'New Opportunity'}
          </h3>
          <p className="text-sm text-text-secondary mb-3">
            {t
              ? 'Implementar panel de consumidores propio para insights en tiempo real.'
              : 'Implement own consumer panel for real-time insights.'}
          </p>
          <span className="text-xs text-primary-400">{t ? 'Inversión requerida: $50K' : 'Required investment: $50K'}</span>
        </div>
      </div>
    </div>
  );
}
